import { ELEMENT_IDS, TRANSPARENCY_TOOLBAR_HEIGHT } from '@/content/values/ed-presentation-mode.values';
import { getElementViewportSize } from '@/utils/dom-utils';
import { ITeacherSize } from '@/core/interfaces/presentation-mode-status.interface';

class EdPresentationModeResize {

    /**
     * Resizes the page to match presenter's viewport while maintaining aspect ratio
     * and maximizing available space
     * 
     * @param teacherSize Object containing the presenter's viewport dimensions and offsets
     * @returns Object with calculated width and height values
     */
    public changePageSize(teacherSize: ITeacherSize): any {
        const componentElement = document.getElementById(ELEMENT_IDS.COMPONENT);

        let { width: availableWidth, height: availableHeight } = getElementViewportSize(componentElement);
        const widthOffset = teacherSize.widthOffset || 0;
        const heightOffset = teacherSize.heightOffset || 0;


        const pageSizeInfo = { height: '', width: '', ...teacherSize };

        // Calculate presenter's aspect ratio
        const presenterAspectRatio = (teacherSize.teacherWidth - widthOffset) / (teacherSize.teacherHeight - heightOffset);

        // Calculate dimensions if constrained by width
        const heightIfWidthConstrained = ((availableWidth - widthOffset) / presenterAspectRatio) + heightOffset;

        // Calculate dimensions if constrained by height
        const widthIfHeightConstrained = (availableHeight - heightOffset) * presenterAspectRatio + widthOffset;

        let newWidth: number;
        let newHeight: number;

        // Choose the constraint that keeps viewport within available space and maximizes usage
        if (heightIfWidthConstrained <= availableHeight) {
            // Width-constrained (fit to width)
            newWidth = availableWidth;
            newHeight = heightIfWidthConstrained;
        } else if (widthIfHeightConstrained <= availableWidth) {
            // Height-constrained (fit to height)
            newHeight = availableHeight;
            newWidth = widthIfHeightConstrained;
        } else {
            // Both constraints fit, choose the one that maximizes space usage
            if (heightIfWidthConstrained / availableHeight > widthIfHeightConstrained / availableWidth) {
                newWidth = availableWidth;
                newHeight = heightIfWidthConstrained;
            } else {
                newHeight = availableHeight;
                newWidth = widthIfHeightConstrained;
            }
        }

        pageSizeInfo.height = `${newHeight}px`;
        pageSizeInfo.width = `${newWidth}px`;
        return pageSizeInfo;
    }

    /**
     * Calculates the scale factor to apply to an image so that it appears
     * in the same proportion to its container as it does in the presenter's view
     * 
     * @param presenterContainerWidth Width of the presenter's container
     * @param imageWidth Natural width of the image
     * @returns The scale factor to apply to the image
     */
    public calculateImageScale(presenterContainerWidth: number, imageWidth: number): number {
        const componentElement = document.getElementById(ELEMENT_IDS.COMPONENT);
        if (!componentElement) {
            return 1;
        }

        const {width: viewerContainerWidth} = getElementViewportSize(componentElement);
        const presenterImageWidth = imageWidth >= presenterContainerWidth ? presenterContainerWidth : imageWidth;        
        const realScale = (presenterImageWidth * viewerContainerWidth) / (presenterContainerWidth * imageWidth);
        return realScale;
    }
}

export default new EdPresentationModeResize();
